import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSchemaGraph,
} from './utils/seo';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';

const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/blog/manifest.json`;

function formatDate(isoDate) {
  if (!isoDate) return '';
  // Parse YYYY-MM-DD as local calendar date (avoid UTC midnight day-shift)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate).trim());
  if (match) {
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function brandifyMerra(text) {
  if (!text) return text;
  // Replace standalone 'Merra' with 'Merra AI' while preserving domains like 'Merra.ai'
  return text.replace(/\bMerra\b(?!\s*AI)(?!\.)/g, 'Merra AI');
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function postMatchesQuery(post, query) {
  const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = normalizeSearchText(
    [post.title, post.excerpt, post.urlSlug, post.slug].filter(Boolean).join(' ')
  );

  return terms.every((term) => haystack.includes(term));
}

function BlogIndex() {
  const { darkMode } = useDarkMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState(() => searchParams.get('q') || '');

  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setPosts(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const nextQuery = searchParams.get('q') || '';
    setQuery((current) => (current === nextQuery ? current : nextQuery));
  }, [searchParams]);

  const filteredPosts = useMemo(
    () => posts.filter((post) => postMatchesQuery(post, query)),
    [posts, query]
  );

  const handleSearchChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);

    const trimmed = nextQuery.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchParams({}, { replace: true });
  };

  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif',
    lineHeight: '1.8',
    color: darkMode ? '#e0e0e0' : '#333',
    backgroundColor: darkMode ? '#1a1a1a' : 'white',
    minHeight: '100vh',
  };

  const linkStyle = {
    color: darkMode ? '#4A9EFF' : '#1976d2',
    textDecoration: 'none',
    cursor: 'pointer',
  };

  const h1Style = {
    fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: darkMode ? '#ffffff' : '#2c3e50',
    fontSize: '42px',
    fontWeight: 'bold',
    lineHeight: '1.2',
    marginBottom: '20px',
  };

  const searchWrapStyle = {
    marginBottom: '28px',
  };

  const searchInputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    fontSize: '16px',
    lineHeight: '1.4',
    borderRadius: '10px',
    border: darkMode ? '1px solid #444' : '1px solid #d0d7de',
    backgroundColor: darkMode ? '#242424' : '#fff',
    color: darkMode ? '#f5f5f5' : '#222',
    outline: 'none',
  };

  const searchMetaStyle = {
    marginTop: '10px',
    fontSize: '14px',
    color: darkMode ? '#888' : '#777',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  };

  const clearButtonStyle = {
    border: 'none',
    background: 'none',
    padding: 0,
    color: darkMode ? '#4A9EFF' : '#1976d2',
    cursor: 'pointer',
    fontSize: '14px',
  };

  const listItemStyle = {
    marginBottom: '28px',
    paddingBottom: '28px',
    borderBottom: darkMode ? '1px solid #333' : '1px solid #eee',
  };

  const titleStyle = {
    fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '20px',
    fontWeight: 'bold',
    color: darkMode ? '#ffffff' : '#2c3e50',
    marginBottom: '8px',
  };

  const excerptStyle = {
    fontSize: '16px',
    color: darkMode ? '#b0b0b0' : '#666',
    lineHeight: '1.6',
  };

  const dateStyle = {
    fontSize: '14px',
    color: darkMode ? '#888' : '#999',
    marginBottom: '6px',
  };

  const hasQuery = query.trim().length > 0;

  return (
    <>
      <Helmet>
        <title>{formatPageTitle('AI Video Editing Blog – Tips, Guides & Resources')}</title>
        <meta name="description" content={formatDescription('Articles on AI video editing, UGC creation, content strategy, and how Merra AI helps creators post consistently without spending hours editing.')} />
        <link rel="canonical" href="https://www.merra.ai/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={formatPageTitle('AI Video Editing Blog – Tips, Guides & Resources')} />
        <meta property="og:description" content={formatDescription('Articles on AI video editing, UGC creation, content strategy, and how Merra AI helps creators post consistently.')} />
        <meta property="og:url" content="https://www.merra.ai/blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={formatPageTitle('AI Video Editing Blog – Tips, Guides & Resources')} />
        <meta name="twitter:description" content={formatDescription('Articles on AI video editing, UGC creation, content strategy, and how Merra AI helps creators post consistently.')} />
      </Helmet>
      <JsonLd
        schema={buildSchemaGraph([
          buildOrganizationSchema(),
          buildWebSiteSchema(),
          buildWebPageSchema({
            name: formatPageTitle('AI Video Editing Blog – Tips, Guides & Resources'),
            description: 'Articles on AI video editing, UGC creation, and content strategy.',
            url: `${SITE_URL}/blog`,
          }),
        ])}
      />
      <div style={containerStyle}>
        <div style={{ marginBottom: '30px' }}>
          <Link to="/" style={linkStyle}>← Back to Home</Link>
        </div>

        <h1 style={h1Style}>Blog</h1>

        {!loading && !error && posts.length > 0 && (
          <div style={searchWrapStyle}>
            <label htmlFor="blog-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              Search blog posts
            </label>
            <input
              id="blog-search"
              type="search"
              value={query}
              onChange={handleSearchChange}
              placeholder="Search articles…"
              autoComplete="off"
              style={searchInputStyle}
            />
            <div style={searchMetaStyle}>
              <span>
                {hasQuery
                  ? `${filteredPosts.length} result${filteredPosts.length === 1 ? '' : 's'}`
                  : `${posts.length} articles`}
              </span>
              {hasQuery && (
                <button type="button" onClick={clearSearch} style={clearButtonStyle}>
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {loading && (
          <p style={{ color: darkMode ? '#b0b0b0' : '#666' }}>Loading posts…</p>
        )}

        {error && (
          <p style={{ color: darkMode ? '#e0e0e0' : '#333' }}>
            Could not load the blog. Please try again later.
          </p>
        )}

        {!loading && !error && posts.length === 0 && (
          <p style={{ color: darkMode ? '#b0b0b0' : '#666' }}>No posts yet.</p>
        )}

        {!loading && !error && posts.length > 0 && filteredPosts.length === 0 && (
          <p style={{ color: darkMode ? '#b0b0b0' : '#666' }}>
            No articles match “{query.trim()}”.
          </p>
        )}

        {!loading && !error && filteredPosts.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredPosts.map((post) => (
              <li key={post.slug} style={listItemStyle}>
                <Link to={`/blog/${post.urlSlug || post.slug}`} style={linkStyle}>
                  <div style={titleStyle}>{brandifyMerra(post.title)}</div>
                  {post.date && (
                    <div style={dateStyle}>{formatDate(post.date)}</div>
                  )}
                  {post.excerpt && (
                    <div style={excerptStyle}>{brandifyMerra(post.excerpt)}</div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        <Footer />
      </div>
    </>
  );
}

export default BlogIndex;
